import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { CopyId } from "@/components/copy-id";
import { CreditsDialog } from "@/components/credits-dialog";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  type Club,
  type ClubInvitation,
  type ClubMember,
  type UpdateClubPayload,
  clubsProvider,
} from "@/providers/clubs.provider";
import { type User, usersProvider } from "@/providers/users.provider";
import {
  IconCheck,
  IconChevronLeft,
  IconCoins,
  IconChevronRight,
  IconLoader2,
  IconPencil,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUserPlus,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { formatDate } from "@/lib/formatters";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClubRecommendationsTab } from "./club_recommendations";

// ── Role badge ────────────────────────────────────────────────────────────────

const ROLE_STYLES: Record<ClubMember["role"], string> = {
  owner: "bg-primary/10 text-primary",
  admin:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  member: "bg-muted text-muted-foreground",
};

function RoleBadge({ role }: { role: ClubMember["role"] }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ROLE_STYLES[role]}`}
    >
      {role}
    </span>
  );
}

// ── User search input ─────────────────────────────────────────────────────────

function UserSearchInput({ onSelect }: { onSelect: (user: User) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setShowResults(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await usersProvider.search(q);
        setResults(data.slice(0, 8));
        setShowResults(true);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <div className="relative">
      <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 150)}
        placeholder="Search by username…"
        className="pl-8 pr-8"
      />
      {loading && (
        <IconLoader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 animate-spin text-muted-foreground" />
      )}
      {showResults && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md overflow-hidden">
          {results.map((user) => (
            <button
              key={user.user_id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(user);
                setQuery("");
                setResults([]);
                setShowResults(false);
              }}
            >
              <p className="font-medium">@{user.username}</p>
              {(user.first_name || user.last_name) && (
                <p className="text-xs text-muted-foreground">
                  {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                </p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Members tab ───────────────────────────────────────────────────────────────

function MembersTab({ clubId }: { clubId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const queryKey = ["club-members", clubId, page];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => clubsProvider.listMembers(clubId, page),
  });

  const members = data?.data;
  const hasNext = data?.has_next ?? false;

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      clubsProvider.adminRemoveMember(clubId, userId),
    onSuccess: (_, userId) => {
      queryClient.setQueryData<{ data: ClubMember[]; has_next: boolean }>(
        queryKey,
        (prev) =>
          prev
            ? { ...prev, data: prev.data.filter((m) => m.user_id !== userId) }
            : prev,
      );
      toast.success("Member removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))
      ) : !members?.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
          <IconUsers className="size-8 opacity-40" />
          <p className="text-sm">No members.</p>
        </div>
      ) : (
        <>
          {members.map((m) => (
            <div
              key={m.club_member_id}
              className="flex items-center justify-between rounded-lg border px-3 py-2 gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  @{m.username}
                  {(m.first_name || m.last_name) && (
                    <span className="text-muted-foreground font-normal ml-1.5">
                      {[m.first_name, m.last_name].filter(Boolean).join(" ")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Joined {formatDate(m.joined_at)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <RoleBadge role={m.role} />
                {m.role !== "owner" && (
                  <ConfirmDestructiveDialog
                    confirmValue={m.user_id}
                    confirmLabel="user ID"
                    title={`Remove @${m.username} from this club?`}
                    description={`This will revoke @${m.username}'s membership in club ${clubId}. This action cannot be undone.`}
                    onConfirm={() => removeMutation.mutate(m.user_id)}
                    isPending={removeMutation.isPending}
                    trigger={({ disabled }) => (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        disabled={disabled}
                        title="Remove member"
                      >
                        <IconX className="size-3.5" />
                      </Button>
                    )}
                  />
                )}
              </div>
            </div>
          ))}
          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="gap-1"
              >
                <IconChevronLeft className="size-3.5" />
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1"
              >
                Next
                <IconChevronRight className="size-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Invitations tab ───────────────────────────────────────────────────────────

function InvitationsTab({ clubId }: { clubId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [usernameFilter, setUsernameFilter] = useState("");
  const queryKey = ["club-invitations", clubId, page];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => clubsProvider.listInvitations(clubId, page),
  });

  const hasNext = data?.has_next ?? false;
  const invitations = usernameFilter
    ? data?.data.filter((inv) =>
        inv.username.toLowerCase().includes(usernameFilter.toLowerCase()),
      )
    : data?.data;

  const inviteMutation = useMutation({
    mutationFn: () =>
      clubsProvider.sendInvitation(clubId, selectedUser!.user_id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["club-invitations", clubId],
      });
      toast.success(`Invitation sent to @${selectedUser?.username}`);
      setSelectedUser(null);
      setShowInviteForm(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (invId: string) =>
      clubsProvider.cancelInvitation(clubId, invId),
    onSuccess: (_, invId) => {
      queryClient.setQueryData<{ data: ClubInvitation[]; has_next: boolean }>(
        queryKey,
        (prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.filter((i) => i.invitation_id !== invId),
              }
            : prev,
      );
      toast.success("Invitation cancelled");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-3">
      {showInviteForm ? (
        <div className="rounded-lg border p-3 flex flex-col gap-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Invite player
          </p>
          {selectedUser ? (
            <div className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-muted/30">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">@{selectedUser.username}</p>
                <p className="font-mono text-xs text-muted-foreground truncate">
                  {selectedUser.user_id}
                </p>
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedUser(null)}
              >
                <IconX className="size-4" />
              </button>
            </div>
          ) : (
            <UserSearchInput onSelect={setSelectedUser} />
          )}
          <div className="flex gap-2">
            <Button
              className="flex-1 gap-1.5"
              disabled={!selectedUser || inviteMutation.isPending}
              onClick={() => inviteMutation.mutate()}
            >
              {inviteMutation.isPending && (
                <IconLoader2 className="size-4 animate-spin" />
              )}
              Send invitation
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowInviteForm(false);
                setSelectedUser(null);
              }}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="self-start gap-1.5"
          onClick={() => setShowInviteForm(true)}
        >
          <IconUserPlus className="size-3.5" />
          Invite player
        </Button>
      )}

      <div className="relative">
        <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={usernameFilter}
          onChange={(e) => setUsernameFilter(e.target.value)}
          placeholder="Filter by username…"
          className="pl-8 h-8 text-sm"
        />
      </div>

      {isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))
      ) : !invitations?.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
          <p className="text-sm">
            {usernameFilter ? "No matching invitations." : "No pending invitations."}
          </p>
        </div>
      ) : (
        <>
          {invitations.map((inv) => (
            <div
              key={inv.invitation_id}
              className="flex items-center justify-between rounded-lg border px-3 py-2 gap-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  @{inv.username}
                  {(inv.first_name || inv.last_name) && (
                    <span className="text-muted-foreground font-normal ml-1.5">
                      {[inv.first_name, inv.last_name].filter(Boolean).join(" ")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Sent {formatDate(inv.created_at)} · Expires{" "}
                  {formatDate(inv.expires_at)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate(inv.invitation_id)}
                title="Cancel invitation"
              >
                <IconX className="size-3.5" />
              </Button>
            </div>
          ))}
          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="gap-1"
              >
                <IconChevronLeft className="size-3.5" />
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1"
              >
                Next
                <IconChevronRight className="size-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Requests tab ──────────────────────────────────────────────────────────────

function RequestsTab({ clubId }: { clubId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const queryKey = ["club-requests", clubId, page];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => clubsProvider.listRequests(clubId, page),
  });

  const requests = data?.data;
  const hasNext = data?.has_next ?? false;

  const respondMutation = useMutation({
    mutationFn: ({ invId, accept }: { invId: string; accept: boolean }) =>
      clubsProvider.respondToRequest(clubId, invId, accept),
    onSuccess: (_, { invId, accept }) => {
      queryClient.setQueryData<{ data: ClubInvitation[]; has_next: boolean }>(
        queryKey,
        (prev) =>
          prev
            ? { ...prev, data: prev.data.filter((r) => r.invitation_id !== invId) }
            : prev,
      );
      toast.success(accept ? "Request approved" : "Request declined");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))
      ) : !requests?.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
          <p className="text-sm">No pending join requests.</p>
        </div>
      ) : (
        <>
          {requests.map((req) => (
            <div
              key={req.invitation_id}
              className="flex items-center justify-between rounded-lg border px-3 py-2 gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  @{req.username}
                  {(req.first_name || req.last_name) && (
                    <span className="text-muted-foreground font-normal ml-1.5">
                      {[req.first_name, req.last_name].filter(Boolean).join(" ")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Requested {formatDate(req.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7 text-green-600 hover:text-green-700 hover:border-green-600"
                  disabled={respondMutation.isPending}
                  onClick={() =>
                    respondMutation.mutate({
                      invId: req.invitation_id,
                      accept: true,
                    })
                  }
                  title="Approve"
                >
                  <IconCheck className="size-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive hover:border-destructive"
                  disabled={respondMutation.isPending}
                  onClick={() =>
                    respondMutation.mutate({
                      invId: req.invitation_id,
                      accept: false,
                    })
                  }
                  title="Decline"
                >
                  <IconX className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="gap-1"
              >
                <IconChevronLeft className="size-3.5" />
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1"
              >
                Next
                <IconChevronRight className="size-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Club edit form ────────────────────────────────────────────────────────────

function ClubEditForm({
  club,
  onSaved,
  onCancel,
}: {
  club: Club;
  onSaved: (updated: Club) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(club.club_name);
  const [description, setDescription] = useState(
    club.club_description ?? "",
  );
  const [countryCode, setCountryCode] = useState(club.country_code ?? "");
  const [primaryColor, setPrimaryColor] = useState(club.primary_color);
  const [secondaryColor, setSecondaryColor] = useState(club.secondary_color);
  const [autoAccept, setAutoAccept] = useState(club.auto_accept_requests);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateClubPayload) =>
      clubsProvider.update(club.club_id, payload),
    onSuccess: (updated) => {
      toast.success("Club updated");
      onSaved(updated);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Club name is required");
      return;
    }
    updateMutation.mutate({
      club_name: name.trim(),
      club_description: description.trim() || null,
      country_code: countryCode.trim().toUpperCase() || null,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      auto_accept_requests: autoAccept,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="club-name">Club name</Label>
        <Input
          id="club-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="club-desc">Description</Label>
        <Textarea
          id="club-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="resize-none"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="club-country">Country code</Label>
        <Input
          id="club-country"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          maxLength={3}
          placeholder="e.g. BRA"
        />
        <p className="text-xs text-muted-foreground">ISO 3166-1 alpha-3</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Primary color</Label>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-9 w-12 cursor-pointer p-1"
            />
            <Input
              type="text"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#000000"
              className="flex-1"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Secondary color</Label>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              className="h-9 w-12 cursor-pointer p-1"
            />
            <Input
              type="text"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              placeholder="#FFFFFF"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
        <div>
          <p className="text-sm font-medium">Auto-accept requests</p>
          <p className="text-xs text-muted-foreground">
            Approve join requests automatically
          </p>
        </div>
        <Switch checked={autoAccept} onCheckedChange={setAutoAccept} />
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          type="submit"
          className="flex-1"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending && (
            <IconLoader2 className="size-4 animate-spin mr-1.5" />
          )}
          Save changes
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ── Club detail sheet ─────────────────────────────────────────────────────────

function ClubDetailSheet({
  club,
  onClose,
  onUpdated,
}: {
  club: Club;
  onClose: () => void;
  onUpdated: (updated: Club) => void;
}) {
  const queryClient = useQueryClient();
  const [detailTab, setDetailTab] = useState<
    "info" | "members" | "invitations" | "requests"
  >("info");
  const [isEditing, setIsEditing] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);

  const { data: requests } = useQuery({
    queryKey: ["club-requests", club.club_id, 1],
    queryFn: () => clubsProvider.listRequests(club.club_id, 1),
  });

  const deleteMutation = useMutation({
    mutationFn: () => clubsProvider.adminDelete(club.club_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      toast.success("Club deleted");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <>
      <SheetHeader>
        <div className="flex items-start justify-between pr-4 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="size-4 rounded shrink-0 border"
              style={{ backgroundColor: club.primary_color }}
            />
            <SheetTitle className="leading-snug truncate">
              {club.club_name}
            </SheetTitle>
          </div>
          {!isEditing && detailTab === "info" && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 mt-0.5"
              onClick={() => setIsEditing(true)}
              title="Edit club"
            >
              <IconPencil className="size-3.5" />
            </Button>
          )}
        </div>
        <SheetDescription className="flex items-center gap-2 flex-wrap">
          <span className="text-xs tabular-nums">
            {club.member_count} members
          </span>
          {club.country_code && (
            <span className="text-xs">· {club.country_code}</span>
          )}
          {club.auto_accept_requests && (
            <span className="rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 text-xs">
              Auto-accept on
            </span>
          )}
        </SheetDescription>
      </SheetHeader>

      <div className="flex border-b px-4">
        {(
          ["info", "members", "invitations", "requests"] as const
        ).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setDetailTab(tab);
              setIsEditing(false);
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium capitalize transition-colors ${
              detailTab === tab
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab}
            {tab === "requests" && requests && requests.data.length > 0 && (
              <span className="ml-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-px tabular-nums text-xs">
                {requests.data.length}
                {requests.has_next ? "+" : ""}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {detailTab === "info" && isEditing && (
          <ClubEditForm
            club={club}
            onSaved={(updated) => {
              onUpdated(updated);
              queryClient.invalidateQueries({ queryKey: ["clubs"] });
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
          />
        )}

        {detailTab === "info" && !isEditing && (
          <div className="flex flex-col gap-3 text-sm">
            {club.club_description && (
              <p className="text-muted-foreground">{club.club_description}</p>
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-lg border p-3">
              <div>
                <p className="text-xs text-muted-foreground">Club ID</p>
                <CopyId id={club.club_id} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Owner ID</p>
                <CopyId id={club.owner_user_id} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Members</p>
                <p className="font-medium tabular-nums">{club.member_count}</p>
              </div>
              {club.country_code && (
                <div>
                  <p className="text-xs text-muted-foreground">Country</p>
                  <p className="font-medium">{club.country_code}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(club.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Auto-accept</p>
                <p className="font-medium">
                  {club.auto_accept_requests ? "Yes" : "No"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <div
                className="size-8 rounded border shrink-0"
                style={{ backgroundColor: club.primary_color }}
                title={club.primary_color}
              />
              <div>
                <p className="text-xs text-muted-foreground">Primary</p>
                <p className="font-mono text-xs">{club.primary_color}</p>
              </div>
              <div
                className="size-8 rounded border shrink-0 ml-3"
                style={{ backgroundColor: club.secondary_color }}
                title={club.secondary_color}
              />
              <div>
                <p className="text-xs text-muted-foreground">Secondary</p>
                <p className="font-mono text-xs">{club.secondary_color}</p>
              </div>
            </div>
          </div>
        )}

        {detailTab === "members" && <MembersTab clubId={club.club_id} />}
        {detailTab === "invitations" && (
          <InvitationsTab clubId={club.club_id} />
        )}
        {detailTab === "requests" && <RequestsTab clubId={club.club_id} />}
      </div>

      <SheetFooter className="flex-col gap-2 sm:flex-col">
        <Button
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => setCreditsOpen(true)}
        >
          <IconCoins className="size-4" />
          Adjust member credits
        </Button>
        <ConfirmDestructiveDialog
          confirmValue={club.club_id}
          confirmLabel="club ID"
          title={`Delete "${club.club_name}"?`}
          description="This will permanently delete the club along with all members and invitations. This action cannot be undone."
          onConfirm={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
          trigger={({ disabled }) => (
            <Button
              variant="destructive"
              className="w-full gap-1.5"
              disabled={disabled}
            >
              <IconTrash className="size-4" />
              Delete club
            </Button>
          )}
        />
        <Button variant="outline" className="w-full" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>

      <CreditsDialog
        open={creditsOpen}
        onOpenChange={setCreditsOpen}
        target={{
          kind: "club",
          id: club.club_id,
          name: club.club_name,
          memberCount: club.member_count,
        }}
      />
    </>
  );
}

// ── Clubs table ───────────────────────────────────────────────────────────────

function ClubsTable() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);

  const isSearching = searchQuery.trim().length > 0;
  const queryKey = ["clubs", page, searchQuery];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      clubsProvider.adminList(page, isSearching ? searchQuery : undefined),
  });

  const clubs = data?.data;
  const hasNext = data?.has_next ?? false;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery !== searchInput) setPage(1);
    setSearchQuery(searchInput);
  }

  function handleClearSearch() {
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clubsProvider.adminDelete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<{ data: Club[]; has_next: boolean }>(
        queryKey,
        (prev) =>
          prev
            ? { ...prev, data: prev.data.filter((c) => c.club_id !== id) }
            : prev,
      );
      toast.success("Club deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <form
          onSubmit={handleSearch}
          className="flex items-center gap-2 flex-1"
        >
          <div className="relative flex-1 max-w-sm">
            <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search clubs by name…"
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary" className="gap-1.5">
            <IconSearch className="size-3.5" />
            Search
          </Button>
          {isSearching && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={handleClearSearch}
            >
              <IconX className="size-3.5" />
              Clear
            </Button>
          )}
        </form>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => queryClient.invalidateQueries({ queryKey })}
        >
          <IconRefresh className="size-3.5" />
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Club ID</TableHead>
              <TableHead className="text-right">Members</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Colors</TableHead>
              <TableHead>Auto-accept</TableHead>
              <TableHead>Created</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load clubs. Check your connection and try again.
                </TableCell>
              </TableRow>
            ) : !clubs?.length ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-12 text-center text-muted-foreground"
                >
                  {page > 1 ? "No more clubs on this page." : "No clubs found."}
                </TableCell>
              </TableRow>
            ) : (
              clubs.map((club) => (
                <TableRow
                  key={club.club_id}
                  className="cursor-pointer"
                  onClick={() => setSelectedClub(club)}
                >
                  <TableCell className="font-medium max-w-[180px] truncate">
                    {club.club_name}
                  </TableCell>
                  <TableCell>
                    <CopyId id={club.club_id} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {club.member_count}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {club.country_code ?? (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div
                        className="size-5 rounded border"
                        style={{ backgroundColor: club.primary_color }}
                        title={club.primary_color}
                      />
                      <div
                        className="size-5 rounded border"
                        style={{ backgroundColor: club.secondary_color }}
                        title={club.secondary_color}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        club.auto_accept_requests
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {club.auto_accept_requests ? "On" : "Off"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(club.created_at)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ConfirmDestructiveDialog
                      confirmValue={club.club_id}
                      confirmLabel="club ID"
                      title={`Delete "${club.club_name}"?`}
                      description="This will permanently delete the club along with all members and invitations. This action cannot be undone."
                      onConfirm={() => deleteMutation.mutate(club.club_id)}
                      isPending={deleteMutation.isPending}
                      trigger={({ disabled }) => (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-destructive hover:text-destructive"
                          disabled={disabled}
                          title="Delete club"
                        >
                          <IconTrash className="size-4" />
                        </Button>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {(page > 1 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}

      <Sheet
        open={selectedClub !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedClub(null);
        }}
      >
        <SheetContent
          side="right"
          className="sm:max-w-md w-full flex flex-col"
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {selectedClub && (
            <ClubDetailSheet
              club={selectedClub}
              onClose={() => setSelectedClub(null)}
              onUpdated={(updated) => setSelectedClub(updated)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── All members table ─────────────────────────────────────────────────────────

function AllMembersTable() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const queryKey = ["admin-all-members", page];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => clubsProvider.adminListAllMembers(page),
  });

  const members = data?.data;
  const hasNext = data?.has_next ?? false;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => queryClient.invalidateQueries({ queryKey })}
        >
          <IconRefresh className="size-3.5" />
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Club ID</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load members.
                </TableCell>
              </TableRow>
            ) : !members?.length ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-12 text-center text-muted-foreground"
                >
                  No members found.
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow key={m.club_member_id}>
                  <TableCell>
                    <p className="font-medium text-sm">@{m.username}</p>
                    {(m.first_name || m.last_name) && (
                      <p className="text-xs text-muted-foreground">
                        {[m.first_name, m.last_name].filter(Boolean).join(" ")}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <CopyId id={m.club_id} />
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={m.role} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(m.joined_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {(page > 1 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── All invitations table ─────────────────────────────────────────────────────

const STATUS_STYLES: Record<ClubInvitation["status"], string> = {
  pending:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  accepted:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  declined: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
};

function AllInvitationsTable() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [type, setType] = useState<"invite" | "request" | undefined>(
    undefined,
  );
  const [status, setStatus] = useState<
    "pending" | "accepted" | "declined" | "cancelled" | undefined
  >(undefined);
  const [usernameInput, setUsernameInput] = useState("");
  const [usernameQuery, setUsernameQuery] = useState("");

  const queryKey = ["admin-all-invitations", page, type, status, usernameQuery];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      clubsProvider.adminListAllInvitations({
        type,
        status,
        username: usernameQuery || undefined,
        page,
      }),
  });

  const invitations = data?.data;
  const hasNext = data?.has_next ?? false;

  function handleTypeFilter(value: typeof type) {
    setType(value);
    setPage(1);
  }

  function handleStatusFilter(value: typeof status) {
    setStatus(value);
    setPage(1);
  }

  function handleUsernameSearch(e: React.FormEvent) {
    e.preventDefault();
    if (usernameQuery !== usernameInput) setPage(1);
    setUsernameQuery(usernameInput);
  }

  function handleClearUsername() {
    setUsernameInput("");
    setUsernameQuery("");
    setPage(1);
  }

  const TYPE_FILTERS: { label: string; value: typeof type }[] = [
    { label: "All", value: undefined },
    { label: "Invites", value: "invite" },
    { label: "Requests", value: "request" },
  ];

  const STATUS_FILTERS: { label: string; value: typeof status }[] = [
    { label: "All", value: undefined },
    { label: "Pending", value: "pending" },
    { label: "Accepted", value: "accepted" },
    { label: "Declined", value: "declined" },
    { label: "Cancelled", value: "cancelled" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleUsernameSearch} className="flex items-center gap-2">
          <div className="relative">
            <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="Search by username…"
              className="pl-8 h-8 text-sm w-48"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary" className="gap-1.5 h-8">
            <IconSearch className="size-3.5" />
            Search
          </Button>
          {usernameQuery && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5 h-8"
              onClick={handleClearUsername}
            >
              <IconX className="size-3.5" />
              Clear
            </Button>
          )}
        </form>

        <div className="flex items-center gap-1">
          {TYPE_FILTERS.map((f) => (
            <button
              key={String(f.value)}
              type="button"
              onClick={() => handleTypeFilter(f.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                type === f.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={String(f.value)}
              type="button"
              onClick={() => handleStatusFilter(f.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                status === f.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => queryClient.invalidateQueries({ queryKey })}
          >
            <IconRefresh className="size-3.5" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Club</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load invitations.
                </TableCell>
              </TableRow>
            ) : !invitations?.length ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-12 text-center text-muted-foreground"
                >
                  No invitations found.
                </TableCell>
              </TableRow>
            ) : (
              invitations.map((inv) => (
                <TableRow key={inv.invitation_id}>
                  <TableCell>
                    <p className="font-medium text-sm">@{inv.username}</p>
                    {(inv.first_name || inv.last_name) && (
                      <p className="text-xs text-muted-foreground">
                        {[inv.first_name, inv.last_name]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm max-w-[160px] truncate">
                    {inv.club_name}
                  </TableCell>
                  <TableCell>
                    <span className="rounded-full px-2 py-0.5 text-xs font-medium capitalize bg-muted text-muted-foreground">
                      {inv.type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[inv.status]}`}
                    >
                      {inv.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(inv.created_at)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(inv.expires_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {(page > 1 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const PAGE_TABS = ["clubs", "members", "invitations", "recommendations"] as const;
type PageTab = (typeof PAGE_TABS)[number];

export function ClubsPage() {
  const [tab, setTab] = useState<PageTab>("clubs");

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Clubs" />
      <div className="flex-1 overflow-auto px-4 lg:px-6">
        <div className="flex overflow-x-auto border-b mb-4 no-scrollbar lg:mb-6">
          {PAGE_TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`-mb-px shrink-0 border-b-2 px-4 py-3 text-sm font-medium capitalize transition-colors ${
                tab === t
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="pb-6">
          {tab === "clubs" && <ClubsTable />}
          {tab === "members" && <AllMembersTable />}
          {tab === "invitations" && <AllInvitationsTable />}
          {tab === "recommendations" && <ClubRecommendationsTab />}
        </div>
      </div>
    </div>
  );
}
